import { httpClient } from '@/api/httpClient';
import {
  mapPaginatedResource,
  type PaginatedApiResource,
  type PaginatedResult,
} from '@/api/pagination.types';
import {
  mapApiResourceToInvitation,
  mapApiResourceToInvitationWithLink,
  type Invitation,
  type InvitationApiResource,
  type InvitationWithLink,
  type InvitationWithLinkApiResource,
} from '@/api/invitations/invitationsApi.types';
import {
  mapApiResourceToTenantMember,
  type TenantMember,
  type TenantMemberApiResource,
} from '@/api/tenants/tenantsApi.types';
import type { WorkspaceInvitationInput } from './teamApi.types';

const MEMBERS_PATH = '/me/workspace/members';
const INVITATIONS_PATH = '/me/workspace/invitations';

export async function fetchWorkspaceMembers(): Promise<TenantMember[]> {
  const response = await httpClient.get<TenantMemberApiResource[]>(MEMBERS_PATH);
  return response.data.map(mapApiResourceToTenantMember);
}

export async function removeWorkspaceMember(userId: string): Promise<void> {
  await httpClient.delete(MEMBERS_PATH + '/' + userId);
}

export async function fetchWorkspaceInvitations(
  page: number,
  pageSize: number
): Promise<PaginatedResult<Invitation>> {
  const response = await httpClient.get<PaginatedApiResource<InvitationApiResource>>(
    INVITATIONS_PATH,
    { params: { page, page_size: pageSize } }
  );
  return mapPaginatedResource(response.data, mapApiResourceToInvitation);
}

export async function createWorkspaceInvitation(
  input: WorkspaceInvitationInput
): Promise<InvitationWithLink> {
  const body: Record<string, unknown> = { email: input.email };
  if (input.fullName !== null && input.fullName.trim().length > 0) {
    body.full_name = input.fullName.trim();
  }
  const response = await httpClient.post<InvitationWithLinkApiResource>(INVITATIONS_PATH, body);
  return mapApiResourceToInvitationWithLink(response.data);
}

export async function resendWorkspaceInvitation(invitationId: string): Promise<InvitationWithLink> {
  const response = await httpClient.post<InvitationWithLinkApiResource>(
    INVITATIONS_PATH + '/' + invitationId + '/resend'
  );
  return mapApiResourceToInvitationWithLink(response.data);
}

export async function revokeWorkspaceInvitation(invitationId: string): Promise<Invitation> {
  const response = await httpClient.post<InvitationApiResource>(
    INVITATIONS_PATH + '/' + invitationId + '/revoke'
  );
  return mapApiResourceToInvitation(response.data);
}
